/**
 * The `beforeSend` step for Sentry (plan 6p, the DPO's conditions): an error report keeps the
 * account id and what is needed to find the bug, never personal data. Emails, phone numbers,
 * tokens and signed-link parameters are replaced in every text, fields with a personal name
 * (email, name, address, password…) are filtered, and the request keeps no cookies, headers with
 * credentials or body.
 *
 * CommonJS, so the server (server/log.js) and the browser (src/util/log.js) use the same rules.
 */

const FILTERED = '[Filtered]';
const MAX_DEPTH = 8;

// Field names that hold personal data or secrets, anywhere in the event
const SENSITIVE_KEY = /(pass(word)?|secret|token|auth|cookie|session|e-?mail|phone|address|first_?name|last_?name|display_?name|full_?name|birth|card|iban|ssn|api_?key|signature|^sig$|^ip$|ip_address)/i;

// URL parameters that carry a secret or a person (signed links, unsubscribe, invites), in a URL or in
// a bare query string, which is how Sentry's `request.query_string` holds them
const SENSITIVE_PARAM = /((?:^|[?&])(?:token|code|secret|key|sig|signature|email|e|t|invite|unsubscribe)=)[^&#\s]*/gi;

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const BEARER = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/g;
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
// A phone number: + or a digit, then at least 9 digits with spaces, dots, dashes or brackets
const PHONE = /(?:\+|\b)\d[\d\s().-]{8,}\d\b/g;
// A Stripe secret or restricted key, whole or as Stripe's 401 and 403 messages mask it
const STRIPE_KEY = /\b[sr]k_(test|live)_[A-Za-z0-9*.]+/g;

const scrubStripeKeys = str => str.replace(STRIPE_KEY, FILTERED);

const scrubString = str =>
  scrubStripeKeys(str)
    .replace(JWT, FILTERED)
    .replace(BEARER, `$1 ${FILTERED}`)
    .replace(EMAIL, FILTERED)
    .replace(SENSITIVE_PARAM, `$1${FILTERED}`)
    .replace(PHONE, FILTERED);

/** Returns a copy of `value` with personal data filtered out of its texts and fields. */
const scrubValue = (value, depth = 0) => {
  if (typeof value === 'string') {
    return scrubString(value);
  }
  if (!value || typeof value !== 'object' || depth > MAX_DEPTH) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(v => scrubValue(v, depth + 1));
  }
  return Object.keys(value).reduce((scrubbed, key) => {
    scrubbed[key] = SENSITIVE_KEY.test(key) ? FILTERED : scrubValue(value[key], depth + 1);
    return scrubbed;
  }, {});
};

const scrubRequest = request => {
  if (!request) {
    return request;
  }
  // No body, cookies or credentials; the URL and query keep their shape without the secrets
  const { data, cookies, headers, env, ...rest } = request;
  const safeHeaders = headers
    ? Object.keys(headers).reduce((kept, name) => {
        kept[name] = SENSITIVE_KEY.test(name) ? FILTERED : scrubValue(headers[name]);
        return kept;
      }, {})
    : undefined;
  return scrubValue({ ...rest, ...(safeHeaders ? { headers: safeHeaders } : {}) });
};

/**
 * Sentry's `beforeSend`: the user is the account id only, the
 * request has no body, cookies or credentials, and every text is scrubbed.
 */
const scrubEvent = event => {
  if (!event) {
    return event;
  }
  const { user, request, breadcrumbs, ...rest } = event;
  const scrubbed = scrubValue(rest);
  if (user && user.id) {
    scrubbed.user = { id: user.id };
  }
  if (request) {
    scrubbed.request = scrubRequest(request);
  }
  if (breadcrumbs) {
    scrubbed.breadcrumbs = scrubValue(breadcrumbs);
  }
  return scrubbed;
};

module.exports = { scrubEvent, scrubString, scrubStripeKeys, FILTERED };
